import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import connectApi from '../../api/connectApi'
import {
    constants,
    sendMessageToWorker,
    sendMessageToWorkerWithResponse,
    fetchProductPictures,
    formatPayloadToClientObject
} from '../../helpers'
import {
    EnrollmentInterface,
    ClientDetailsPayloadInterface,
    EnrollmentEnum,
    EnrollmentPayloadType,
    CardPaymentPayloadType,
    PartialClientDetailsPayload
} from '../../storeTypes'
import {
    ZoneInfoTemplate,
    ProductInfo,
    SelectedPacket,
    GpsCoords,
    ProductPriceType
} from '../../components/enrollForm/enrollFormTypes'
import { ErrorResponse } from '../../types'
import { buildOrderEvents } from 'helpers/buildOrderEvents'
import { ClientEnrollmentData } from 'storeTypes/enrollmentTypes'

export const enrollmentAction = createAsyncThunk(
    'enrollment/enrollmentAction',
    async ({ zoneSFID }: EnrollmentInterface, { rejectWithValue }) => {
        const { ENROLLMENT } = constants.endpoints
        try {
            const params: { zone_sf_id?: string } = {}
            if (zoneSFID) params.zone_sf_id = zoneSFID

            const response = await connectApi.get(ENROLLMENT, { params })

            const listOfProductImageURLs = response.data.flatMap(
                (item: ZoneInfoTemplate) =>
                    item.product_list.map(
                        (product: ProductInfo) => product.image_url
                    )
            )
            fetchProductPictures(listOfProductImageURLs)
            return response.data
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue(error.response.data.detail)
            }
            return rejectWithValue('something went wrong')
        }
    }
)

export const storeEnrollmentFormAction = createAsyncThunk(
    'enrollment/storeEnrollmentFormAction',
    async ({ zoneSFID }: EnrollmentInterface) => {
        const { ENROLLMENT } = constants.endpoints
        try {
            const params: { zone_sf_id?: string } = {}
            if (zoneSFID) params.zone_sf_id = zoneSFID
            const response = await connectApi.get(ENROLLMENT, { params })
            if (response.status === 200) {
                localStorage.setItem(
                    constants.ENROLLMENT_FORM_CACHED_NAME,
                    'true'
                )
            }
        } catch (response: unknown) {
            console.error(response)
        }
    }
)

const enrollmentErrorText = (payload: unknown) => {
    if (typeof payload === 'string') return payload
    if (
        payload &&
        typeof payload === 'object' &&
        'detail' in payload &&
        typeof (payload as { detail?: unknown }).detail === 'string'
    ) {
        return (payload as { detail: string }).detail
    }
    return ''
}

const rejectHttpResponse = (
    response: { status?: number; data?: { detail?: unknown } },
    rejectWithValue: (value: unknown) => unknown
) => {
    if (
        !response ||
        typeof response.status !== 'number' ||
        response.status < 400
    ) {
        return null
    }
    return rejectWithValue({
        httpStatus: response.status,
        detail: response.data?.detail
    })
}

export const updatePackagesAction = createAsyncThunk(
    'enrollment/updatePackagesAction',
    async (
        { clientDetails, existingOrders, newOrders, skipLocalPersist }:
            ClientEnrollmentData,
        { rejectWithValue, dispatch }
    ) => {
        try {
            const { ORDERS } = constants.endpoints

            clientDetails.payment_progress =
                clientDetails.all_enrollment && clientDetails.balance
                    ? (Number(clientDetails.balance) /
                          Number(clientDetails.all_enrollment)) *
                      100
                    : 0

            dispatch({ type: EnrollmentEnum.UPDATING_PACKAGES })
            dispatch({
                type: 'enrollment/setEnrollmentPayload',
                payload: clientDetails
            })

            if (!skipLocalPersist) {
                sendMessageToWorker({
                    'update client details with orders': clientDetails
                })
            }

            const events = buildOrderEvents(
                existingOrders,
                newOrders,
                clientDetails.client_code
            )

            const dropPromises = events.drop.map((event) =>
                connectApi.put(ORDERS, event)
            )
            const createPromises = events.create.map((event) =>
                connectApi.post(ORDERS, event)
            )
            const responses = await Promise.all([
                ...dropPromises,
                ...createPromises
            ])
            const httpFailure = responses
                .map((response) => rejectHttpResponse(response, rejectWithValue))
                .find(Boolean)
            if (httpFailure) return httpFailure

            return true
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue({
                    httpStatus: Number(error.response.data.code) || 400,
                    detail: error.response.data.detail
                })
            }
            return rejectWithValue('something went wrong')
        }
    }
)

const removeUndefinedFields = (
    obj?: Partial<ClientDetailsPayloadInterface>
): ClientDetailsPayloadInterface | undefined => {
    if (!obj) return undefined
    Object.keys(obj).forEach((key) => {
        const typedKey = key as keyof ClientDetailsPayloadInterface
        if (obj[typedKey] === undefined || obj[typedKey] === null) {
            delete obj[typedKey]
        }
    })
    return obj as ClientDetailsPayloadInterface
}

export const clientDetailsEnrollmentAction = createAsyncThunk(
    'enrollment/clientDetailsEnrollmentAction',
    async (
        args:
            | {
                  clientDetailsPayload: Partial<ClientDetailsPayloadInterface>
                  gpsCoords: GpsCoords
              }
            | [
                  {
                      clientDetailsPayload: Partial<ClientDetailsPayloadInterface>
                  },
                  GpsCoords
              ],
        { dispatch, rejectWithValue }
    ) => {
        let clientDetailsPayload:
            | Partial<ClientDetailsPayloadInterface>
            | undefined
        let gpsCoords: GpsCoords

        if (Array.isArray(args)) {
            clientDetailsPayload = args[0]?.clientDetailsPayload
            gpsCoords = args[1]
        } else {
            clientDetailsPayload = args.clientDetailsPayload
            gpsCoords = args.gpsCoords
        }

        const { CLIENT_DETAILS } = constants.endpoints
        try {
            const cleanedPayload = removeUndefinedFields(clientDetailsPayload)
            if (!cleanedPayload) {
                return rejectWithValue('something went wrong')
            }
            clientDetailsPayload = cleanedPayload

            if (clientDetailsPayload.form_type === 'enrollment') {
                const blankIdResponse =
                    clientDetailsPayload.farmerIdBlankId ||
                    (
                        await sendMessageToWorkerWithResponse({
                            'get blank id': 'client needs a blank id'
                        })
                    ).blank_id.code.toString()

                if (blankIdResponse) {
                    clientDetailsPayload.client_code = blankIdResponse

                    const clientVisitStore = localStorage.getItem(
                        constants.CLIENT_FOR_VISIT_STORE
                    )
                    if (clientVisitStore) {
                        const parsedVisitStorage = JSON.parse(clientVisitStore)
                        parsedVisitStorage.clientCode =
                            clientDetailsPayload.client_code
                        localStorage.setItem(
                            constants.CLIENT_FOR_VISIT_STORE,
                            JSON.stringify(parsedVisitStorage)
                        )
                    }

                    dispatch({
                        type: 'enrollment/setClientDetailsPayload',
                        payload: { ...clientDetailsPayload, gpsCoords }
                    })

                    sendMessageToWorker({
                        'delete blank id': clientDetailsPayload.client_code
                    })
                }
            }

            const response = await connectApi.post(
                CLIENT_DETAILS,
                clientDetailsPayload
            )
            const httpFailure = rejectHttpResponse(response, rejectWithValue)
            if (httpFailure) return httpFailure

            return response.data
        } catch (error: unknown) {
            if (error instanceof ErrorResponse) {
                return rejectWithValue({
                    httpStatus: Number(error.response.data.code) || 400,
                    detail: error.response.data.detail
                })
            }
            return rejectWithValue('something went wrong')
        }
    }
)

interface EnrollmentLocalState {
    client?: ClientDetailsPayloadInterface
    orders?: {
        selectedProduct?: ProductInfo
        selectedPackets?: SelectedPacket[]
        total?: string
    }
    payments?: CardPaymentPayloadType
}

interface EnrollmentState {
    productListData?: []
    loading: boolean
    preFetchLoading: boolean
    error: string | null
    response?: [] | string | null
    updatePackages: EnrollmentPayloadType
    clientDetails: EnrollmentPayloadType
    localState: EnrollmentLocalState
}

const initialState: EnrollmentState = {
    productListData: [],
    loading: true,
    preFetchLoading: false,
    error: null,
    response: null,
    updatePackages: {},
    clientDetails: { response: null, loading: false, error: null },
    localState: {}
}

type formatEnrollmentType = {
    product_list: [
        {
            hide_price: string | boolean
            category: string
            unit: string
            product_prices: ProductPriceType[]
        }
    ]
}

type formatEnrollmentReturnType = {}

export function formatEnrollmentData(
    data: formatEnrollmentType[]
): formatEnrollmentReturnType[] {
    if (data) {
        data.forEach((item) => {
            item.product_list?.forEach((product) => {
                product.hide_price = product.hide_price === 'True'

                if (product.product_prices?.length) {
                    const price = product.product_prices[0]
                    product.unit = price.units_of_measure
                }
            })
        })
    }
    return data
}

export const enrollmentSlice = createSlice({
    name: 'enrollment',
    initialState,
    reducers: {
        saveClientState: (
            state,
            action: PayloadAction<ClientDetailsPayloadInterface>
        ) => {
            state.localState.client = {
                ...state.localState.client,
                ...action.payload
            }
        },
        selectProduct: (state, action: PayloadAction<ProductInfo>) => {
            state.localState.orders = {
                ...state.localState.orders,
                selectedProduct: action.payload
            }
        },
        addPacket: (state, action: PayloadAction<SelectedPacket>) => {
            const existing = state.localState.orders?.selectedPackets || []
            const updated = [...existing, action.payload]
            const total = updated
                .reduce((acc, p) => acc + Number(p.price), 0)
                .toString()
            state.localState.orders = {
                ...state.localState.orders,
                selectedPackets: updated,
                total
            }
        },
        removePacket: (state, action: PayloadAction<string>) => {
            if (state.localState.orders?.selectedPackets) {
                state.localState.orders.selectedPackets =
                    state.localState.orders.selectedPackets.filter(
                        (packet) => packet.uuid !== action.payload
                    )
            }
        },
        savePaymentState: (
            state,
            action: PayloadAction<CardPaymentPayloadType>
        ) => {
            state.localState.payments = action.payload
        },
        setClientDetailsPayload: (
            state,
            action: PayloadAction<
                PartialClientDetailsPayload & { gpsCoords?: GpsCoords }
            >
        ) => {
            const { gpsCoords, ...clientDetails } = action.payload
            state.clientDetails.clientDetailsPayload = {
                ...(state.clientDetails.clientDetailsPayload ?? {}),
                ...clientDetails
            }
            if (gpsCoords) {
                state.clientDetails.gpsCoords = {
                    ...(state.clientDetails.gpsCoords ?? {}),
                    ...gpsCoords
                }
            }
        },
        setEnrollmentPayload: (
            state,
            action: PayloadAction<EnrollmentPayloadType['enrollmentPayload']>
        ) => {
            state.updatePackages.enrollmentPayload =
                formatPayloadToClientObject(action.payload)
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(enrollmentAction.pending, (state) => {
                state.loading = true
                state.preFetchLoading = false
                state.error = ''
                state.productListData = []
            })
            .addCase(enrollmentAction.fulfilled, (state, action) => {
                formatEnrollmentData(action.payload)
                state.loading = false
                state.preFetchLoading = false
                state.error = ''
                state.productListData = action.payload
            })
            .addCase(enrollmentAction.rejected, (state, action) => {
                state.loading = false
                state.preFetchLoading = false
                state.productListData = []
                state.error = action.payload as string
            })

        builder
            .addCase(updatePackagesAction.pending, (state) => {
                state.updatePackages.loading = true
                state.updatePackages.error = ''
            })
            .addCase(updatePackagesAction.fulfilled, (state) => {
                state.updatePackages.loading = false
                state.updatePackages.error = ''
            })
            .addCase(updatePackagesAction.rejected, (state, action) => {
                state.updatePackages.loading = false
                state.updatePackages.error = enrollmentErrorText(action.payload)
            })

        builder
            .addCase(clientDetailsEnrollmentAction.pending, (state) => {
                state.clientDetails.loading = true
                state.clientDetails.error = ''
            })
            .addCase(
                clientDetailsEnrollmentAction.fulfilled,
                (state, action) => {
                    state.clientDetails.response = action.payload
                    state.clientDetails.loading = false
                }
            )
            .addCase(
                clientDetailsEnrollmentAction.rejected,
                (state, action) => {
                    state.clientDetails.loading = false
                    state.clientDetails.error = enrollmentErrorText(
                        action.payload
                    )
                }
            )
    }
})

export const {
    saveClientState,
    selectProduct,
    addPacket,
    removePacket,
    savePaymentState,
    setClientDetailsPayload,
    setEnrollmentPayload
} = enrollmentSlice.actions

export default enrollmentSlice.reducer
